import { useMemo } from "react";

import queryGQL from "@/Apollo/query/config.graphql";
import { ConfigQuery, ConfigQueryVariables } from "@/Apollo/schema";
import { SUPPORTED_COUNTRIES } from "@/constants/countries";
import {
  LazyQueryHookOptions,
  QueryHookOptions,
  useLazyQuery,
  useQuery,
} from "@apollo/client";

export const useConfig = (
  options?: QueryHookOptions<ConfigQuery, ConfigQueryVariables>
) => {
  return useQuery(queryGQL, options);
};

export const useConfigLazy = (
  options?: LazyQueryHookOptions<ConfigQuery, ConfigQueryVariables>
) => {
  return useLazyQuery(queryGQL, options);
};

/**
 * The gateway's delivery countries in the order the site has always listed
 * them: the listing countries it offers in {@link SUPPORTED_COUNTRIES} order,
 * then any it adds (the US), so a European buyer's country list does not
 * reorder when the answer arrives.
 */
export const orderDeliveryCountries = (
  deliveryCountries: readonly string[]
): readonly string[] => [
  ...SUPPORTED_COUNTRIES.filter(country => deliveryCountries.includes(country)),
  ...deliveryCountries.filter(
    country => !(SUPPORTED_COUNTRIES as readonly string[]).includes(country)
  ),
];

/**
 * WHO-4752. The countries a buyer can have an order delivered to, for their
 * address and phone fields: the gateway's `config.deliveryCountries`, which is
 * the listing countries plus the US while the gateway's `US_CHECKOUT_ENABLED`
 * is on (WHO-4751). Until the gateway answers, or if it cannot, the listing
 * countries: never a country the gateway has not offered.
 *
 * `settled` is false while the gateway has not answered yet, for a field that
 * prefills from the list once (the phone's calling code).
 *
 * Sellers and listings keep {@link SUPPORTED_COUNTRIES}; selling from the US
 * stays closed.
 */
export const useDeliveryCountriesState = (): {
  countries: readonly string[];
  settled: boolean;
} => {
  const { data, loading } = useConfig();
  const deliveryCountries = data?.config.deliveryCountries;

  const countries = useMemo(
    () =>
      deliveryCountries
        ? orderDeliveryCountries(deliveryCountries)
        : SUPPORTED_COUNTRIES,
    [deliveryCountries]
  );

  return { countries, settled: !loading };
};

/** WHO-4752. The buyer's delivery countries; see {@link useDeliveryCountriesState}. */
export const useDeliveryCountries = (): readonly string[] =>
  useDeliveryCountriesState().countries;
